// (Copyright) Confluent, Inc.

import memoizeOne from 'memoize-one';
import sanitize from 'sanitize-html';
import isEqual from 'lodash/isEqual';

export const truncate = (
  input: string,
  threshold: number,
  direction: 'rtl' | 'ltr' | 'mid' = 'ltr'
) => {
  if (input == null) {
    return;
  }
  const thresholdLength = Math.max(0, Math.min(input.length, threshold));
  if (input.length <= thresholdLength) {
    return input;
  }

  switch (direction) {
    case 'rtl':
      return `...${input.slice(-thresholdLength)}`;
    case 'mid':
      const thresholdLengthHalf = Math.floor(thresholdLength / 2);
      // if length is odd, add 1 to 1st half
      const correction = thresholdLength % 2;
      return `${input.slice(0, thresholdLengthHalf + correction)}...${input.slice(
        input.length - thresholdLengthHalf,
        input.length
      )}`;
    case 'ltr':
    default:
      return `${input.slice(0, thresholdLength)}...`;
  }
};

export const cut = (input: string, leadingCount: number, trailingCount: number) => {
  if (input == null) {
    return;
  }
  const lead = input.slice(0, leadingCount);
  if (
    leadingCount >= input.length ||
    trailingCount >= input.length ||
    lead.length + trailingCount >= input.length
  ) {
    return input;
  }
  const tail = input.slice(-trailingCount);
  return `${lead}...${tail}`;
};

export const isEmptyOrWhitespace = (str?: string | null) => str == null || !/\S/.test(str);

const defaultOptions = { allowLinks: false, allowBTags: false };

export const sanitizeHtml = memoizeOne(
  (
    text: string,
    options: {
      allowBTags?: boolean;
      allowLinks: boolean;
    } = defaultOptions
  ): string => {
    const allowedTags = options.allowBTags ? ['b'] : [];

    if (options.allowLinks) {
      allowedTags.push('a');

      const sanitized = sanitize(text, {
        allowedTags,
        allowedAttributes: {
          a: ['href', 'target', 'rel'],
        },
        allowedSchemes: ['https'],
        allowProtocolRelative: false,
        transformTags: {
          a: (_, attributes) => {
            if (!attributes.href || !attributes.href.startsWith('https://')) {
              // Transforming invalid links to a span, but those are not allowed so they it ends
              // up getting converted to text
              return {
                tagName: 'span',
              };
            }

            return {
              tagName: 'a',
              attribs: {
                href: attributes.href,
                target: '_blank',
                rel: 'noopener noreferrer',
              },
            };
          },
        },
      });

      return sanitized;
    } else {
      return sanitize(text, {
        allowedTags,
        allowedAttributes: [],
      });
    }
  },
  isEqual // Use deep equal comparison since options parameter is an object
);

export const nextStringWithIncrementingSuffix = (
  base: string,
  existingStrings: string[],
  separator: string
): string => {
  if (!existingStrings.includes(base)) {
    return base;
  }

  let current = base;

  for (let j = 0; j <= existingStrings.length; j++) {
    current = `${base}${separator}${j + 1}`;

    if (!existingStrings.includes(current)) {
      break;
    }
  }

  return current;
};

const sentenceBoundaryPattern = new RegExp(
  [
    `([.?!])`, // puncation mark indicating end of sentence
    `\\s+`, // whitespace
    `(?=[A-Za-z0-9($])`, // character that you'd expect at start of new sentence
  ].join(''),
  'g'
);

export const splitSentences = (text: string): Array<string> => {
  return text.replace(sentenceBoundaryPattern, (match) => `${match}\r`).split(/\r/);
};

export const capitalizeFirstLetter = (text: string): string => {
  if (text.length === 0) {
    return text;
  }

  const [first, ...rest] = text;
  return first.toLocaleUpperCase() + rest.join('');
};

export const getAcronym = (text: string): string => {
  return text
    .split(' ')
    .map((word) => [...word][0])
    .join('');
};

export const indent = (
  string: string,
  amount: number,
  {
    tabWidth,
  }: {
    tabWidth?: number;
  } = {}
): string => string.padStart(amount * (tabWidth ?? 2) + string.length);
